import { domainRegistry } from "#schemas/registry.ts";
import { z } from "zod";

export const ToolSchema = z
  .enum(["DRAW", "ERASER", "FILL", "SELECT", "LINE", "RECTANGLE", "ELLIPSE"])
  .register(domainRegistry, {
    id: "tool",
    title: "Tool",
    description: "Active drawing tool identifier.",
    surface: "store",
    llmExposed: true,
    group: "tools",
    examples: ["DRAW", "FILL"],
  });
export type Tool = z.infer<typeof ToolSchema>;

/** Value-pun preserving `Tool.DRAW` ergonomics. */
export const Tool = ToolSchema.enum;

export const ToolModeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("brush"), brush: ToolSchema }),
  z.object({ kind: z.literal("picker") }),
]).register(domainRegistry, {
  id: "tool_mode",
  title: "Tool mode",
  description: "Discriminated tool mode: a sized brush (with tool id) or the color picker.",
  surface: "store",
  llmExposed: true,
  group: "tools",
  examples: [{ kind: "brush", brush: "DRAW" }, { kind: "picker" }],
});
export type ToolMode = z.infer<typeof ToolModeSchema>;

/** Tools whose stroke width is controlled by `brushSize`. */
export const SIZED_TOOLS: ReadonlySet<Tool> = new Set<Tool>([
  Tool.DRAW,
  Tool.ERASER,
  Tool.LINE,
  Tool.RECTANGLE,
  Tool.ELLIPSE,
]);

/** Shape tools that have a fill/outline toggle. When filled, brush size is N/A. */
export const FILLABLE_TOOLS: ReadonlySet<Tool> = new Set<Tool>([Tool.RECTANGLE, Tool.ELLIPSE]);

/** True when the brush-size control is meaningful for the current mode + fill state. */
export function usesBrushSize(mode: ToolMode, shapeFill: boolean): boolean {
  if (mode.kind !== "brush") return false;
  if (!SIZED_TOOLS.has(mode.brush)) return false;
  if (shapeFill && FILLABLE_TOOLS.has(mode.brush)) return false;
  return true;
}
